import React from 'react';
import Help from '../../utils/Help'
import { useEffect, useState } from 'react';
import { useMapContext } from '../../contexts/MapContext';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Link
} from '@mui/material';

const Legend = React.memo(() => {
    const { colorScheme } = useMapContext();
    const [helpText, setHelpText] = useState(null);
    ///// LEGEND STRUCT /////
    /**
     * // Example usage:
     * const legendData = {
     *   name: 'Percent Impervious',
     *   colorMappings: [
     *     { color: '#008000', value: '0%' },
     *     { color: '#ffffff', value: '50%' },
     *     { color: '#663300', value: '`100%' },
     *   ]
     * };
     */
    const [legendData, setLegendData] = useState({
      // Currently, the values for outfalls are fixed - so not included in this struct.
      name: null, // Name of variable
      colorMappings: [], // This stores (color, value) pairs
    });

    // Updates the legend data based on the selected color scheme
    useEffect(() => {
      const updateLegendData = () => {
        switch (colorScheme) {
          case 'percent_impervious':
            setLegendData({
              name: 'Percent Impervious',
              colorMappings: [
                { color: '#008000', value: '0%' },
                { color: '#66A266', value: '20%' },
                { color: '#B3CDB3', value: '40%' },
                { color: '#ffffff', value: '50%' },
                { color: '#D9BFB3', value: '60%' },
                { color: '#A66F66', value: '80%' },
                { color: '#663300', value: '100%' },
              ],
            });
            setHelpText(
              <>
                <h3>Percent Impervious: </h3>
                <p>% of subcatchment surface that is impervious.</p>
                <h3>Impervious Surface: (definition from the Detroit Stormwater Management Design Manual)</h3>
                <p>Any surface area that prevents or substantially impedes the entry
                  of water into the soil in the manner that such water entered the soil prior to
                  development, or which causes water to run off the surface in greater quantities or at an
                  increased rate of flow, including but not limited to, roofs, parking lots, compacted gravel
                  and dirt, driveways, sidewalks, and storage areas. (Ordinance) Impervious surfaces
                  include but not limited to such surfaces as rooftops, crushed/compacted stone or gravel
                  in areas exposed to vehicle traffic, asphalt or concrete paving, driveways and parking lots,
                  walkways, patio areas, storage areas, or other surfaces which similarly affect the natural
                  infiltration or runoff patterns existing prior to development. By definition, any surfaces
                  which are driven on, or used for parking, are impervious surfaces.</p>
              </>
            )
            break;

          case 'elevation':
            setLegendData({
              name: 'Elevation (ft)',
              colorMappings: [
                { color: '#00008B', value: '555' },
                { color: '#0000CD', value: '565' },
                { color: '#0000FF', value: '575' },
                { color: '#007FFF', value: '585' },
                { color: '#00FFFF', value: '595' },
                { color: '#7FFF00', value: '605' },
                { color: '#FFFF00', value: '615' },
                { color: '#FF7F00', value: '625' },
                { color: '#FF0000', value: '635' },
                { color: '#8B0000', value: '645' },
              ],
            });
            setHelpText("This is an averaged value over the area of the subcatchment.");
            break;

          case 'soil_type':
            setLegendData({
              name: 'Soil Type',
              colorMappings: [
                { color: '#A0522D', value: 'Clay' },
                { color: '#FFA500', value: 'Sand' },
                { color: '#FF4500', value: 'Loam032' },
                { color: 'violet', value: 'Loam034' },
                { color: 'brown', value: 'Loam037' },
              ],
            });
            setHelpText(
              <>
                <p>This is the primary soil type of each subcatchment.</p>
                <h4>Loam:</h4>
                <p>A mixture of sand, silt, and clay. The number, e.g. 34 in Loam034, represents the porosity. So, 34% of the soil is "empty" and contains air or water.</p>

              </>
            );
            break;

          case 'drawdown':
            setLegendData({
              name: 'Estimated Drainage',
              colorMappings: [
                { color: '#fcfdbf', value: 'Fastest' },
                { color: '#fb8761', value: '' },
                { color: '#b6377a', value: '' },
                { color: '#50127b', value: '' },
                { color: '#000004', value: 'Slowest' },
              ],
            });
            setHelpText(
                <>
                    <p>The calculation for this drawdown rate can be found in <Link target={"_blank"} color={"#8bd3f5"} href={"https://doi.org/10.1039/D3EW00098B"}>this paper.</Link></p>
                </>
            );
            break;
          case 'basementFlooding':
            setLegendData({
              name: 'Basement Flooding',
              colorMappings: [
                { color: '#FF0000', value: 'High Risk' },
                { color: '#55694f', value: 'Low Risk' },
              ],
            });
            setHelpText('We define basement flooding as when the groundwater level reaches 8 feet below the surface.');
            break;

          default:
            setHelpText(null);
            setLegendData({
              name: "",
              colorMappings: [
                  { color: '#dddddd', value: 'Subcatchment' },
              ],
            });
        }
      };

      updateLegendData();
    }, [colorScheme]);

    if (!legendData || !legendData.colorMappings || legendData.colorMappings.length === 0) {
        return null; // Handle empty or null legend data gracefully
    }

    return (
        <Box sx={{
            width: 200,
            backgroundColor: '#071317',
            borderRadius: 2,
            opacity: 0.9,
            padding: 2,
            position: 'absolute',
            top: 15,
            left: 80,
            zIndex: 10,
        }}>

            <Typography  fontFamily="Helvetica" fontWeight="bold" color="rgb(148, 207, 236)" sx={{display: "flex", flexDirection: "row", alignItems: "center"}} gutterBottom>
                {legendData.name} <Help text={helpText}/>
            </Typography>

            <TableContainer component={Paper} sx={{ marginBottom: 1, overflow: 'hidden'}}>
                <Table size="small">
                    <TableBody>
                        {legendData.colorMappings.map((mapping, index) => (
                            <TableRow key={index}>
                                <TableCell sx={{borderBottom: (colorScheme === 'standard') ? 'none' : '1px solid #777'}}>
                                    <svg
                                        width="25"
                                        height="25"
                                        viewBox="0 0 633 537"
                                    >
                                        <g>
                                            <path
                                                fill={mapping.color} // Fill with the appropriate color
                                                stroke="#FFFFFF"
                                                strokeWidth="1"
                                                d="M 27.975858,138.51461 254.51207,63.457433 242.22999,48.445997 354.13342,17.05845 598.41042,330.25159 468.7662,381.42694 504.93011,433.28463 310.46379,523.35324 289.99365,482.41296 274.98221,477.63659 225.85388,373.23888 193.78399,356.86277 155.57306,321.38119 141.24396,292.723 75.739517,246.32402 Z"
                                                />
                                        </g>
                                    </svg>
                                </TableCell>
                                <TableCell sx={{borderBottom: (colorScheme === 'standard') ? 'none' : '1px solid #777'}} >{mapping.value}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {colorScheme !== 'standard' && (
                <>
                <Typography fontFamily="Helvetica" fontWeight="bold" color="rgb(148, 207, 236)" gutterBottom>
                    Outfalls
                </Typography>
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    <Box
                                        sx={{
                                            backgroundColor: "#b78f6c",
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            display: 'inline-block',
                                            border: '1px solid #000',
                                        }}
                                    />
                                </TableCell>
                                <TableCell> Least overflow </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    <Box
                                        sx={{
                                            backgroundColor: "#A52A2A",
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            display: 'inline-block',
                                            border: '1px solid #000',
                                        }}
                                    />
                                </TableCell>
                                <TableCell> Most overflow </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                </>
            )}
            {colorScheme === 'standard' && (
                <>
                    <Typography fontFamily="Helvetica" fontWeight="bold" color="rgb(148, 207, 236)" gutterBottom>

                    </Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{borderBottom: (colorScheme === 'standard') ? 'none' : '1px solid #777'}}>
                                        <Box
                                            sx={{
                                                backgroundColor: "#b78f6c",
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                display: 'inline-block',
                                                border: '1px solid #000',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{borderBottom: (colorScheme === 'standard') ? 'none' : '1px solid #777'}}> Outfall </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

        </Box>
    );
});

export default Legend;

